/* eslint-disable react-hooks/rules-of-hooks */
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import Suspense from '../../shared/components/suspense';
import { StorageUtil } from '../../shared/utils/storage';
import toaster, { Theme } from '../../shared/toaster';
import {
  getIsRequestSucceeded,
  redirectWithoutRefresh,
} from '../../shared/utils';
import TableLoadingSkeleton from './components/table/table-loading-skeleton';
import SearchingEmpty from './components/searching-empty';
import RecentSearch from './components/recent-search/recent-search';
import NoSearchResult from './components/no-search-result/no-search-result';
import NoSearchResultIcon from '../../shared/components/images/no-search-result-icon';
import { useLeadFinder } from './components/hooks/useLeadFinder';
import { useSavedLeadFilter } from './components/hooks/useLeadSavedFilter';
import { useLeadApiState } from './components/hooks/useLeadApiState';
import { useCompanyFinder } from './components/hooks/useCompanyFinder';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
  BulkLeadsActionType,
  LeadRevealType,
} from './types';
import {
  SAVED_TAB_BULK_OPERATION_LIMIT,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
  computeLeadRevealCredits,
  delay,
} from './helpers/helper';
import { RootState } from '../../store/root-reducer';
import { getTeamsRequest } from '../settings/components/users-and-teams/extra-actions';
import {
  leadFinderBulkOperationLimit,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../shared/utils/user-details';
import retryLazy from '../retry-lazy';
import V2LeadHeader from './components/header/v2-lead-header';
import V2LeadSidebar from './components/sidebar/v2-lead-sidebar';
import {
  setSidebarExpanded,
  setShowAiSearchPopup,
  setBulkPeopleSearch,
  clearBulkPeopleSearch,
  setBulkPeopleModeActive,
  openCreditNotAvailableModal,
  closeCreditNotAvailableModal,
  setSelectedLeads,
  setIsSelectedAllLeads,
  setDeSelectedLeads,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
  setShowBulkRevealTagModal,
  setShowRemoveFromListModal,
  setShowGlobalRestrictionModal,
  setShowSelectedMoreThanLimitModal,
  openAddToSequenceModal,
  closeAddToSequenceModal,
  closeAiChatPanel,
  openAiChatPanel,
  setActiveChatConversationId,
  clearAiChatPendingQuery,
  resetAiChatInitialView,
} from './v2-lead-slice';
import {
  leadAddToProspectAPI,
  leadAddToSequenceAPI,
} from './helpers/leads.api';
import { getPostLoadMetaRequest } from '../home/extra-actions';
import { enableBulkActionRequestPolling } from '../home/home-slice';
import { LEAD_LISTING_MESSAGES } from './helpers/leads-message';
import NoResultGradientImage from '../../shared/components/images/no-result-gradient';
import sageIcon from '../../assets/images/sage-icon.webp';
import { getTagsFilterApplied } from '../prospect/helpers/helper';
import { useV2LeadBulkActions } from './components/hooks/useV2LeadBulkActions';
import { useV2LeadReveal } from './components/hooks/useV2LeadReveal';
import { useCompaniesBulkActions } from './components/hooks/useCompaniesBulkActions';
import { useV2CompanyReveal } from './components/hooks/useV2CompanyReveal';
import { useBulkReveal } from './components/hooks/useBulkReveal';
import { useLeadFinderEffects } from './components/hooks/useLeadFinderEffects';
import { useListManagement } from './components/hooks/useListManagement';
import { useRowActions } from './components/hooks/useRowActions';
import { useTabNavigation } from './components/hooks/useTabNavigation';
import { AnalyticsEvents } from '../../shared/enums/analytics';
import { Routes } from '../../shared/routes';
import hasPermission from '../../shared/utils/access-control/has-permission';
import { Permissions } from '../../shared/utils/access-control/enums/permissions';
import { useProspectDetailsFromUrl } from '../../shared/hooks/use-prospect-details-from-url';
import { PageHeaderAlertTypes } from '../../shared/utils/page-header-alert-types';

const V2LeadTable = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-table" */ './components/table/v2-lead-table'
      ),
  ),
);
const V2CompanyTable = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-company-table" */ './components/table/v2-company-table'
      ),
  ),
);
const AiSearchPopup = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-ai-search" */ './components/ai-search-popup/ai-search-popup'
      ),
  ),
);
const AddToSequenceModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-add-to-sequence" */ '../prospect/components/prospect-list/components/modals/add-to-sequence-modal'
      ),
  ),
);
const ProspectDetails = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-prospect-details" */ '../prospect/components/prospect-list/components/prospect-details'
      ),
  ),
);
const BulkRevealTagModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/bulk-reveal-tag-modal'
      ),
  ),
);
const CreditNotAvailableModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/credit-not-available-modal'
      ),
  ),
);
const SelectedMoreThanLimitModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/selected-more-than-limit-modal'
      ),
  ),
);
const WhatsNewModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-whats-new" */ './components/whats-new-modal'
      ),
  ),
);
const GlobalRestrictionModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-global-restriction" */ '../home/components/global-restriction-modal'
      ),
  ),
);

const AiChatPanel = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-ai-chat-panel" */ './components/ai-chat-panel/ai-chat-panel'
      ),
  ),
);

const isPrimaryTab = (value?: string): value is LeadFinderPrimaryTab =>
  !!value &&
  Object.values(LeadFinderPrimaryTab).includes(value as LeadFinderPrimaryTab);

const noResultsDescription = (
  <div>
    Results matching this query could not be displayed. Please try refining your
    search <br /> or clearing some of your filters.
  </div>
);

const noSavedLeadsDescription = (
  <div>
    You haven't saved any leads yet. Search for leads and save them to see them
    here.
  </div>
);

const V2Lead: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { tab, subtab } = useParams<{ tab?: string; subtab?: string }>();
  const {
    isSidebarExpanded,
    activeLeadType,
    activeSubTab,
    showAiSearchPopup,
    bulkPeopleSearch,
    selection,
    modals,
    aiChat,
  } = useSelector((state: RootState) => state.leadFinderV2);

  const {
    showPanel: showAiChatPanel,
    pendingQuery: aiChatPendingQuery,
    initialView: aiChatInitialView,
  } = aiChat;

  const {
    selectedLeads,
    isSelectedAllLeads,
    deSelectedLeads,
    selectedCompanies,
    isSelectedAllCompanies,
  } = selection;

  const {
    showBulkRevealTagModal,
    showRemoveFromListModal,
    showGlobalRestrictionModal,
    showSelectedMoreThanLimitModal,
    showCreditNotAvailableModal,
    creditCheckRevealType,
    showAddToSequenceModal,
    selectedLeadIdForSequence,
  } = modals;
  const loginUserEmail = useSelector((state: RootState) => state?.home?.email);
  const hasV2LeadFinderAccess = useSelector(
    (state: RootState) => state?.home?.hasV2LeadFinderAccess,
  );
  const leadFinder = useSelector((state: RootState) => state?.home?.leadFinder);
  const getUserSettingsRequestStatus = useSelector(
    (state: RootState) => state?.home?.getUserSettingsRequest?.status,
  );
  const pageHeaderBannerAlert = useSelector(
    (state: RootState) => state?.pageHeader?.bannerAlert,
  );
  const {
    singleProspect,
    singleProspectSequenceDetails,
    getSingleProspectDetailsRequest,
    getSingleProspectSequencesDetailsRequest,
    prospectsFilters,
  } = useSelector((state: RootState) => state.prospect);

  const leadReveal = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL'
      ],
  );
  const leadRevealBucket1 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET1'
      ],
  );
  const leadRevealBucket2 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET2'
      ],
  );
  const isBucket1Frozen = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isBucket1Frozen,
  );

  const leadCredits1 = computeLeadRevealCredits(
    leadReveal,
    leadRevealBucket1,
    leadRevealBucket2,
    { isBucket1Frozen },
  );

  const leadCredits = leadCredits1.availableCredits;
  const teamsList = useSelector(
    (state: RootState) => state.usersAndTeams.teams,
  );
  const getTeamsListRequestStatus = useSelector(
    (state: RootState) => state.usersAndTeams.getTeamsRequest.status,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state.home.isDemoAccount,
  );
  const [showPhoneNumberNudge, setShowPhoneNumberNudge] = useState(false);
  const [, setIgnorePhoneNumberNudge] = useState(false);
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const refreshLeadsAndStartPollingRef = useRef<() => void>(() => {});
  const maybeEnableNotificationPollingRef = useRef<(apiResponse: any) => void>(
    () => {},
  );
  // When true (bulk search mode), tab change should not retain filters in URL
  const skipUrlPushForBulkSearchRef = useRef(false);
  // Per-tab bulk-search mode (so Company bulk mode isn't lost when switching to People)
  const tabSpecificBulkModeRef = useRef<{ people: boolean; company: boolean }>({
    people: false,
    company: false,
  });

  const [emailRevealingLeads, setEmailRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const [emailPhoneRevealingLeads, setEmailPhoneRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const [enrichingCompanies, setEnrichingCompanies] = useState<
    Record<number, boolean>
  >({});
  const [loadingAddToProspect, setLoadingAddToProspect] = useState<
    Record<number, boolean>
  >({});
  const [loadingAddToSequence, setLoadingAddToSequence] = useState<
    Record<number, boolean>
  >({});
  // Use the reusable hook to handle URL-based prospect details modal
  const {
    selectedProspectId: selectedContactId,
    showProspectDetailsModal,
    defaultTab,
    hideShowProspectDetailsModal: hideShowProspectDetailsModalFromHook,
    openProspectDetailsModal,
  } = useProspectDetailsFromUrl({
    // Dynamic pattern: matches /v2/leads/:anyTab/:anySubtab/:prospectId
    pathnamePattern: /\/v2\/leads\/[^/]+\/[^/]+\/(\d+)/,
  });
  const [isBulkFindPeopleLoading, setIsBulkFindPeopleLoading] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);

  const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();

  // Check if user has access (wait for API to complete)
  const shouldBlockAccess =
    getIsRequestSucceeded(getUserSettingsRequestStatus) &&
    !hasV2LeadFinderAccess;

  const {
    savedFilters,
    setSavedFilters,
    handleDefaultValues,
    handleQuery,
  } = useSavedLeadFilter();
  const { leadTags, isLeadTagsLoading, fetchLeadTags } = useLeadApiState();

  const currentTabFromUrl = isPrimaryTab(tab)
    ? tab
    : LeadFinderPrimaryTab.PEOPLE;
  const isPeopleTabFromUrl = currentTabFromUrl === LeadFinderPrimaryTab.PEOPLE;
  const shouldBlockPeopleFinder = shouldBlockAccess || !isPeopleTabFromUrl;

  const {
    isLoading,
    isLoadingCounts,
    isLoadingSavedCounts,
    leadsData,
    handleRecentSearch,
    handleGeneratePayload,
    setLeadsData,
    handleRouteChange,
    searchLeads,
    handleFetchCounts,
    handlePagination,
    handleAppendLead,
    countsData,
    countsError,
    apiParam,
    savedApiParam,
    peopleListsData,
    companyListsData: peopleTabCompanyListsData,
    isLoadingPeopleLists,
    refreshPeopleLists,
    refreshCompanyLists: refreshCompanyListsForPeopleTab,
    skipNextCountCallRef,
    handleFetchSavedCounts,
  } = useLeadFinder(
    activeSubTab,
    setSavedFilters as React.Dispatch<React.SetStateAction<SavedTabFilters>>,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    shouldBlockPeopleFinder,
    activeLeadType,
    skipUrlPushForBulkSearchRef,
  );

  // Company finder hook - only active when Company tab is selected
  const currentTabFromUrlForCompany = isPrimaryTab(tab)
    ? tab
    : LeadFinderPrimaryTab.PEOPLE;
  const isCompanyTabFromUrl =
    currentTabFromUrlForCompany === LeadFinderPrimaryTab.COMPANY;
  const isCompanyTab = activeLeadType === LeadFinderPrimaryTab.COMPANY;
  const {
    isLoading: isCompanyLoading,
    isLoadingCounts: isLoadingCompanyCounts,
    isLoadingSavedCounts: isLoadingCompanySavedCounts,
    companiesData,
    handleGeneratePayload: handleCompanyGeneratePayload,
    handleRecentSearch: handleCompanyRecentSearch,
    searchCompanies,
    handleFetchCounts: handleCompanyFetchCounts,
    handlePagination: handleCompanyPagination,
    countsData: companyCountsData,
    countsError: companyCountsError,
    companyListsData,
    isLoadingLists: isLoadingCompanyLists,
    refreshCompanyLists,
    savedApiParam: companySavedApiParam,
    apiParam: companyApiParam,
    setCompaniesData,
    getCompanyNames,
    signalsListData,
    newsListData,
  } = useCompanyFinder(
    activeSubTab,
    shouldBlockAccess || !isCompanyTabFromUrl, // Block when not on company tab (check URL, not Redux)
    activeLeadType,
    skipUrlPushForBulkSearchRef,
  );

  // --- useTabNavigation ---
  const {
    preservedFiltersRef,
    tabSpecificFiltersRef,
    handleLeadTypeChange,
    handleSubTabChange,
    handleClearAllPeople,
    handleClearAllCompany,
    getAllFiltersExceptPagination,
  } = useTabNavigation({
    setLeadsData,
    setCompaniesData,
    handleFetchCounts,
    handleCompanyFetchCounts,
    searchLeads,
    searchCompanies,
    handleRouteChange,
    setSavedFilters,
    handleDefaultValues,
    refreshPeopleLists,
    refreshCompanyLists,
    skipNextCountCallRef,
    skipUrlPushForBulkSearchRefProp: skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
  });

  const syncCompanyTabSpecificFiltersRef = useCallback(
    (filters: URLSearchParams) => {
      const normalized = getAllFiltersExceptPagination(
        new URLSearchParams(filters.toString()),
      );
      tabSpecificFiltersRef.current.company = normalized.toString();
    },
    [getAllFiltersExceptPagination, tabSpecificFiltersRef],
  );

  const syncPeopleTabSpecificFiltersRef = useCallback(
    (filters: URLSearchParams) => {
      const normalized = getAllFiltersExceptPagination(
        new URLSearchParams(filters.toString()),
      );
      tabSpecificFiltersRef.current.people = normalized.toString();
    },
    [getAllFiltersExceptPagination, tabSpecificFiltersRef],
  );

  useEffect(() => {
    if (!location.pathname.includes('/v2/leads/company/')) return;
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) return;

    const currentParams = new URLSearchParams(location.search);
    const nonPagination = getAllFiltersExceptPagination(
      currentParams,
    ).toString();

    if (nonPagination) {
      tabSpecificFiltersRef.current.company = nonPagination;
      return;
    }

    const hasPreservedCompanyFilters =
      (tabSpecificFiltersRef.current.company || '').trim().length > 0;
    if (
      !tabSpecificBulkModeRef.current.company &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED &&
      !hasPreservedCompanyFilters
    ) {
      tabSpecificFiltersRef.current.company = '';
    }
  }, [
    activeLeadType,
    activeSubTab,
    location.pathname,
    location.search,
    getAllFiltersExceptPagination,
  ]);

  // --- useLeadFinderEffects ---
  useLeadFinderEffects({
    leadFinder,
    setShowWhatsNewModal,
    shouldBlockAccess,
    preservedFiltersRef,
    tabSpecificFiltersRef,
    skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
    bulkPeopleSearch,
    fetchLeadTags,
    keywordInputRef,
    tab,
    subtab,
  });

  // --- useRowActions ---
  const {
    addToListOpenFromRow,
    setAddToListOpenFromRow,
    companyIdToAddFromRow,
    setCompanyIdToAddFromRow,
    leadIdToAddFromRow,
    setLeadIdToAddFromRow,
    triggerOpenAddTag,
    setTriggerOpenAddTag,
    triggerOpenRemoveTag,
    setTriggerOpenRemoveTag,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    handleLeadRowRemoveFromList,
    handleLeadRowAddTag,
    handleLeadRowRemoveTag,
    handleRowTagModalClosed,
    handleAddToListOpenChange,
    handleSingleLeadAddToList,
    overrideSelectedLeadObjects,
    overrideSelectedCompanyObjects,
  } = useRowActions({
    leadsData,
    companiesData,
    leadTags,
    leadCredits,
    leadFinderBulkOperationLimitValue,
  });

  const handleCompanyTriggerSearchWithFilters = useCallback(
    (filters: URLSearchParams) => {
      const payload = handleCompanyGeneratePayload(filters);
      const normalized = new URLSearchParams(filters.toString());
      ['page', 'start', 'take'].forEach((key) => normalized.delete(key));
      tabSpecificFiltersRef.current.company = normalized.toString();
      if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
        handleCompanyFetchCounts(payload);
      }
      searchCompanies(payload);
      tabSpecificFiltersRef.current.company = filters.toString();
    },
    [
      dispatch,
      handleCompanyGeneratePayload,
      searchCompanies,
      handleCompanyFetchCounts,
      activeSubTab,
      tabSpecificFiltersRef,
    ],
  );

  const handlePeopleTriggerSearchWithFilters = useCallback(
    (filters: URLSearchParams) => {
      const payload = handleGeneratePayload(filters);
      if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
        handleFetchCounts(payload);
      }
      searchLeads(payload);
      tabSpecificFiltersRef.current.people = filters.toString();
    },
    [
      dispatch,
      handleGeneratePayload,
      searchLeads,
      handleFetchCounts,
      handleFetchSavedCounts,
      activeSubTab,
      tabSpecificFiltersRef,
    ],
  );

  const handleBulkFindPeople = useCallback(async () => {
    try {
      setIsBulkFindPeopleLoading(true);
      let companyIds: string[] = [];
      const wasCompanyBulkMode =
        tabSpecificBulkModeRef.current.company ||
        skipUrlPushForBulkSearchRef.current;

      if (isSelectedAllCompanies) {
        const companyFiltersSource = tabSpecificFiltersRef.current.company
          ? new URLSearchParams(tabSpecificFiltersRef.current.company)
          : undefined;
        const companyNamesData = await getCompanyNames(companyFiltersSource);

        if (!Array.isArray(companyNamesData) || companyNamesData.length === 0) {
          toaster.error('No companies found to search for people.');
          setIsBulkFindPeopleLoading(false);
          return;
        }

        companyIds = Array.from(
          new Set(
            companyNamesData
              .map((item) => item.companyId?.toString())
              .filter(Boolean),
          ),
        );
      } else {
        if (!companiesData?.profiles) {
          toaster.error('No companies found to search for people.');
          setIsBulkFindPeopleLoading(false);
          return;
        }

        companyIds = Array.from(
          new Set(
            selectedCompanies
              ?.map((companyId) => {
                const id = companyId;
                return id ? String(id) : null;
              })
              .filter(Boolean) as string[],
          ),
        );
      }

      const currentParams = new URLSearchParams(location.search);
      const companyFiltersFromUrl = getAllFiltersExceptPagination(
        currentParams,
      );
      const hasCompanyFiltersInUrl =
        companyFiltersFromUrl.toString().length > 0;
      const effectiveCompanyFilters = hasCompanyFiltersInUrl
        ? companyFiltersFromUrl.toString()
        : tabSpecificFiltersRef.current.company || '';
      tabSpecificFiltersRef.current.company = effectiveCompanyFilters;
      tabSpecificFiltersRef.current.people = '';

      const searchParams = new URLSearchParams();

      if (isSelectedAllCompanies) {
        const bulkFilters = new URLSearchParams();
        if (companyIds.length > 0) {
          companyIds.forEach((id) => bulkFilters.append('company_ids', id));
        }
        dispatch(setBulkPeopleSearch({ filters: bulkFilters.toString() }));
        dispatch(setBulkPeopleModeActive(true));
        skipUrlPushForBulkSearchRef.current = true;
        if (skipNextCountCallRef) {
          skipNextCountCallRef.current = true;
        }
      } else {
        dispatch(setBulkPeopleModeActive(false));
        dispatch(clearBulkPeopleSearch());
        if (companyIds.length > 0) {
          companyIds.forEach((id) => searchParams.append('company_ids', id));
        }
        tabSpecificFiltersRef.current.people = searchParams.toString();
        await delay();
      }

      history.push(
        `/v2/leads/${LeadFinderPrimaryTab.PEOPLE}/${
          LeadFinderSecondaryTab.TOTAL
        }?${searchParams.toString()}`,
        {
          preservedCompanyFilters: effectiveCompanyFilters,
        } as any,
      );
      tabSpecificBulkModeRef.current.company = wasCompanyBulkMode;
      dispatch(setSelectedCompanies([]));
      dispatch(setIsSelectedAllCompanies(false));
    } catch (error: any) {
      setIsBulkFindPeopleLoading(false);
      if (!error?.isHandled) {
        toaster.error(
          error?.message || 'Failed to get company names. Please try again.',
        );
      }
    }
  }, [
    getCompanyNames,
    history,
    location.search,
    selectedCompanies,
    isSelectedAllCompanies,
    companiesData,
    tabSpecificFiltersRef,
    tabSpecificBulkModeRef,
    skipUrlPushForBulkSearchRef,
  ]);

  const wasBulkPendingRef = useRef(false);
  useEffect(() => {
    if (bulkPeopleSearch.isPending && !wasBulkPendingRef.current) {
      tabSpecificFiltersRef.current.people = bulkPeopleSearch.filters;
    }
    if (wasBulkPendingRef.current && !bulkPeopleSearch.isPending) {
      setIsBulkFindPeopleLoading(false);
    }
    wasBulkPendingRef.current = bulkPeopleSearch.isPending;
  }, [bulkPeopleSearch.isPending, bulkPeopleSearch.filters]);

  useEffect(() => {
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      setIsBulkFindPeopleLoading(false);
    }
  }, [activeLeadType]);

  const userUrlsPeople = useMemo(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
    );
    if (!stored) return [];
    try {
      return JSON.parse(stored)?.[loginUserEmail] || [];
    } catch (_error) {
      return [];
    }
  }, [loginUserEmail]);

  const userUrlsCompany = useMemo(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
    );
    if (!stored) return [];
    try {
      return JSON.parse(stored)?.[loginUserEmail] || [];
    } catch (_error) {
      return [];
    }
  }, [loginUserEmail]);

  const [storedUrlsPeople, setStoredUrlsPeople] = useState<string[]>(
    userUrlsPeople || [],
  );
  const [storedUrlsCompany, setStoredUrlsCompany] = useState<string[]>(
    userUrlsCompany || [],
  );

  // Keep state in sync when login user changes
  useEffect(() => {
    setStoredUrlsPeople(userUrlsPeople || []);
  }, [userUrlsPeople]);
  useEffect(() => {
    setStoredUrlsCompany(userUrlsCompany || []);
  }, [userUrlsCompany]);

  const refreshStoredUrlsPeople = useCallback(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
    );
    if (!stored) {
      setStoredUrlsPeople([]);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setStoredUrlsPeople(parsed?.[loginUserEmail] || []);
    } catch (_error) {
      setStoredUrlsPeople([]);
    }
  }, [loginUserEmail]);

  const refreshStoredUrlsCompany = useCallback(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
    );
    if (!stored) {
      setStoredUrlsCompany([]);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setStoredUrlsCompany(parsed?.[loginUserEmail] || []);
    } catch (_error) {
      setStoredUrlsCompany([]);
    }
  }, [loginUserEmail]);

  // Refresh storedUrls when handleRecentSearch is called
  // We'll wrap handleRecentSearch to also refresh the URLs
  const handleRecentSearchWithRefresh = useCallback(() => {
    handleRecentSearch();
    // Refresh storedUrls after a short delay to ensure localStorage is updated
    setTimeout(() => {
      refreshStoredUrlsPeople();
    }, 100);
  }, [handleRecentSearch, refreshStoredUrlsPeople]);

  const handleCompanyRecentSearchWithRefresh = useCallback(() => {
    handleCompanyRecentSearch?.();
    setTimeout(() => {
      refreshStoredUrlsCompany();
    }, 100);
  }, [handleCompanyRecentSearch, refreshStoredUrlsCompany]);

  const handleSelectAllAcrossPages = useCallback(
    (isSelected: boolean) => {
      if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
        dispatch(setIsSelectedAllLeads(isSelected));
        if (!isSelected) {
          // When deselecting, also clear selections
          dispatch(setSelectedLeads([]));
          dispatch(setDeSelectedLeads([]));
        }
      } else if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
        dispatch(setIsSelectedAllCompanies(isSelected));
        if (isSelected) {
          // When selecting all, select all companies on current page
          const currentPageCompanyIds = (
            companiesData?.profiles || []
          ).map((company) => Number(company.id || company.company_id || 0));
          dispatch(setSelectedCompanies(currentPageCompanyIds));
        } else {
          // When deselecting, also clear selections
          dispatch(setSelectedCompanies([]));
        }
      }
    },
    [activeLeadType, dispatch, companiesData?.profiles],
  );

  // Bulk actions hook
  const {
    loadingBulkAction: leadLoadingBulkAction,
    handleBulkAddToSequence,
    handleBulkAction: handleBulkActionFromHook,
    tagButtonProps,
  } = useV2LeadBulkActions({
    selectedLeads,
    setSelectedLeads: (val) => dispatch(setSelectedLeads(val as number[])),
    isSelectedAllLeads,
    setIsSelectedAllLeads: (val) =>
      dispatch(setIsSelectedAllLeads(val as boolean)),
    deSelectedLeads,
    setDeSelectedLeads: (val) => dispatch(setDeSelectedLeads(val as number[])),
    activeSubTab,
    handleGeneratePayload,
    searchLeads,
    setShowAddToSequenceModal: (val) =>
      dispatch(val ? openAddToSequenceModal(null) : closeAddToSequenceModal()),
    leadTags,
    savedFilters,
    setSavedFilters,
    handleQuery,
    leadsData,
    fetchLeadTags,
  });

  const {
    loadingBulkAction: companyLoadingBulkAction,
    handleCompanyExport,
    companyTagButtonProps,
  } = useCompaniesBulkActions({
    activeLeadType,
    selectedCompanies,
    isSelectedAllCompanies,
    savedFilters,
    companySavedApiParam,
    searchCompanies,
    fetchLeadTags,
    companiesData,
    leadTags,
    setSavedFilters,
    handleQuery,
    setSelectedCompanies: (val) =>
      dispatch(setSelectedCompanies(val as number[])),
    setIsSelectedAllCompanies: (val) =>
      dispatch(setIsSelectedAllCompanies(val as boolean)),
  });

  const loadingBulkAction =
    activeLeadType === LeadFinderPrimaryTab.PEOPLE
      ? leadLoadingBulkAction
      : {
          loadingAddToProspect: false,
          loadingAddToSequence: false,
          loadingExport: companyLoadingBulkAction.loadingExport,
        };

  const handlePeopleGeneratePayloadWithBulkFallback = useCallback(
    (source?: string | URLSearchParams) => {
      if (source === undefined) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('page');
        urlParams.delete('start');
        urlParams.delete('take');
        const urlHasFilters = urlParams.toString().length > 0;
        if (!urlHasFilters && tabSpecificFiltersRef.current.people) {
          return handleGeneratePayload(tabSpecificFiltersRef.current.people);
        }
      }
      return handleGeneratePayload(source);
    },
    [handleGeneratePayload, tabSpecificFiltersRef],
  );

  // Lead reveal hook
  const {
    handleRevealLead,
    handleBulkReveal,
    refreshLeadsAndStartPolling,
    selectAllRevealType,
  } = useV2LeadReveal({
    selectedLeads,
    isSelectedAllLeads,
    deSelectedLeads,
    leadsData,
    handleGeneratePayload: handlePeopleGeneratePayloadWithBulkFallback,
    setLeadsData,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    emailRevealingLeads,
    emailPhoneRevealingLeads,
    setIsSelectedAllLeads: (val) =>
      dispatch(setIsSelectedAllLeads(val as boolean)),
    setSelectedLeads: (val) => dispatch(setSelectedLeads(val as number[])),
    setDeSelectedLeads: (val) => dispatch(setDeSelectedLeads(val as number[])),
    activeSubTab,
    apiParam,
    savedApiParam,
    isLoading,
  });

  const maybeEnableNotificationPolling = useCallback(
    (apiResponse: any) => {
      if (apiResponse?.payload?.shouldPoll) {
        dispatch(enableBulkActionRequestPolling());
      }
    },
    [dispatch],
  );

  refreshLeadsAndStartPollingRef.current = refreshLeadsAndStartPolling;
  maybeEnableNotificationPollingRef.current = maybeEnableNotificationPolling;

  // Company reveal hook
  const {
    handleRevealCompany: handleRevealCompanyFromHook,
    handleBulkRevealResponse,
  } = useV2CompanyReveal({
    companiesData,
    handleGeneratePayload: handleCompanyGeneratePayload,
    setCompaniesData,
    setEnrichingCompanies,
    enrichingCompanies,
    activeSubTab,
    apiParam: companyApiParam,
    savedApiParam: companySavedApiParam,
    isLoading: isCompanyLoading,
  });

  const handleRevealCompanyWithCreditCheck = useCallback(
    (companyId: number) => {
      if (Number(leadCredits) < 0.1) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.COMPANY));
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      handleRevealCompanyFromHook(companyId);
    },
    [leadCredits, handleRevealCompanyFromHook, dispatch],
  );

  // --- useListManagement ---
  const {
    isCreateListLoading,
    isDeleteListLoading,
    loadingRemoveFromList,
    handleCreateListOnly,
    handleSaveList,
    handleDeleteList,
    handleExportList,
    handleAddToList,
    handleRemoveFromList,
  } = useListManagement({
    activeLeadType,
    activeSubTab,
    leadsData,
    setLeadsData,
    apiParam,
    savedApiParam,
    handleGeneratePayload: handlePeopleGeneratePayloadWithBulkFallback,
    searchLeads,
    refreshPeopleLists,
    refreshCompanyListsForPeopleTab,
    companiesData,
    setCompaniesData,
    companySavedApiParam,
    handleCompanyGeneratePayload,
    searchCompanies,
    refreshCompanyLists,
    savedFilters,
    setSavedFilters,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    refreshLeadsAndStartPollingRef,
    maybeEnableNotificationPollingRef,
    leadCredits,
    isDemoAccount,
    isCompanyTab,
    handleCompanyTriggerSearchWithFilters,
    handleBulkRevealResponse,
    setEnrichingCompanies,
    companyApiParam,
    handleFetchSavedCounts,
  });

  // --- useBulkReveal ---
  const {
    isBulkRevealing,
    tagsToAdd,
    setTagsToAdd,
    setLeadRevealType,
    handleBulkRevealWithTags,
    handleBulkRevealSubmit,
  } = useBulkReveal({
    handleBulkReveal,
    leadsData,
    leadCredits,
    activeSubTab,
  });

  const hasPaymentPendingBanner =
    pageHeaderBannerAlert === PageHeaderAlertTypes.PAYMENT_PENDING;

  const mainContentClass = classNames('v2-lead-content', {
    'v2-lead-content--sidebar-collapsed': !isSidebarExpanded,
    'v2-lead-content--with-payment-banner': hasPaymentPendingBanner,
  });

  const redirectToPeopleWithCompanyIds = useCallback(
    (companyIds: string[]) => {
      skipUrlPushForBulkSearchRef.current = false;
      dispatch(setBulkPeopleModeActive(false));
      dispatch(clearBulkPeopleSearch());

      const currentParams = new URLSearchParams(location.search);
      const companyFiltersFromUrl = getAllFiltersExceptPagination(
        currentParams,
      );
      tabSpecificFiltersRef.current.company =
        companyFiltersFromUrl.toString().length > 0
          ? companyFiltersFromUrl.toString()
          : tabSpecificFiltersRef.current.company || '';
      const searchParams = new URLSearchParams();
      companyIds.forEach((id) => searchParams.append('company_ids', id));
      tabSpecificFiltersRef.current.people = searchParams.toString();
      history.push(
        `/v2/leads/${LeadFinderPrimaryTab.PEOPLE}/${
          LeadFinderSecondaryTab.TOTAL
        }?${searchParams.toString()}`,
      );
    },
    [location.search, getAllFiltersExceptPagination, history, dispatch],
  );

  // Company table callbacks (extracted from inline to stabilize references)
  const handleCompanySelect = useCallback(
    (companyId: number) => {
      const prevSet = new Set(selectedCompanies);
      if (prevSet?.has(companyId)) {
        if (isSelectedAllCompanies) {
          dispatch(setIsSelectedAllCompanies(false));
        }
        dispatch(
          setSelectedCompanies(
            selectedCompanies.filter((id) => id !== companyId),
          ),
        );
      } else if (selectedCompanies.length < 10000) {
        dispatch(setSelectedCompanies([...selectedCompanies, companyId]));
      }
    },
    [isSelectedAllCompanies, selectedCompanies, dispatch],
  );

  const handleCompanySelectAll = useCallback(
    (companyIds: number[]) => {
      dispatch(setSelectedCompanies(companyIds));
      dispatch(setIsSelectedAllCompanies(false));
    },
    [dispatch],
  );

  const handleCompanyPaginationChange = useCallback(
    (page: number, limit: number) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('page');
      urlParams.delete('start');
      urlParams.delete('take');
      const filterSource =
        !urlParams.toString() && tabSpecificFiltersRef.current.company
          ? tabSpecificFiltersRef.current.company
          : undefined;
      handleCompanyPagination({ page, limit, filterSource });
    },
    [handleCompanyPagination, tabSpecificFiltersRef],
  );

  const handleSaveCompanyFromRow = useCallback((companyId: number) => {
    companyIdToAddFromRowRef.current = companyId;
    setCompanyIdToAddFromRow(companyId);
    setAddToListOpenFromRow(true);
  }, []);

  const handleFindPeople = useCallback(
    (company: any) => {
      const companyId = company?.company_id;
      if (!companyId) {
        toaster.error('Company ID not found.');
        return;
      }
      redirectToPeopleWithCompanyIds([String(companyId)]);
    },
    [redirectToPeopleWithCompanyIds],
  );

  const handleRowAddToList = useCallback((companyId: number) => {
    companyIdToAddFromRowRef.current = companyId;
    setCompanyIdToAddFromRow(companyId);
    setAddToListOpenFromRow(true);
  }, []);

  const handleRowRemoveFromList = useCallback(
    (companyId: number) => {
      dispatch(setSelectedCompanies([companyId]));
      dispatch(setIsSelectedAllCompanies(false));
      dispatch(setShowRemoveFromListModal(true));
    },
    [dispatch],
  );

  const handleRowAddTag = useCallback(
    (companyId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedCompanies([companyId]));
        dispatch(setIsSelectedAllCompanies(false));
      }
      setTriggerOpenAddTag(companyId);
    },
    [dispatch, setTriggerOpenAddTag],
  );

  const handleRowRemoveTag = useCallback(
    (companyId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedCompanies([companyId]));
        dispatch(setIsSelectedAllCompanies(false));
      }
      setTriggerOpenRemoveTag(companyId);
    },
    [dispatch, setTriggerOpenRemoveTag],
  );

  // Table handlers - optimized with Set for O(1) lookup
  const handleLeadSelect = useCallback(
    (leadId: number) => {
      const prevSet = new Set(selectedLeads);
      if (prevSet.has(leadId)) {
        dispatch(setSelectedLeads(selectedLeads.filter((id) => id !== leadId)));
      } else if (selectedLeads.length < 10000) {
        dispatch(setSelectedLeads([...selectedLeads, leadId]));
      }
    },
    [selectedLeads, dispatch],
  );

  const handleLeadSelectAll = useCallback(
    (leadIds: number[]) => {
      // Replace selection with the provided IDs
      dispatch(setSelectedLeads(leadIds));
      dispatch(setIsSelectedAllLeads(false));
      dispatch(setDeSelectedLeads([]));
    },
    [dispatch],
  );

  const hideShowProspectDetailsModal = useCallback(() => {
    hideShowProspectDetailsModalFromHook();
  }, [hideShowProspectDetailsModalFromHook]);

  const handleLeadClick = useCallback(
    (leadId: number) => {
      if (!hasPermission(Permissions.PROSPECT_READ)) {
        return;
      }
      openProspectDetailsModal(leadId);
    },
    [openProspectDetailsModal],
  );

  const handleRevealEmail = useCallback(
    (leadId: number) => {
      // Credit check for single lead reveal
      if (Number(leadCredits) <= 0) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.EMAIL));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      setEmailRevealingLeads((prev) => ({
        ...prev,
        [leadId]: true,
      }));
      // Call reveal - the state update will trigger a re-render
      handleRevealLead(leadId, LeadRevealType.EMAIL);
    },
    [handleRevealLead, leadCredits, setEmailRevealingLeads, dispatch],
  );

  const handleRevealEmailPhone = useCallback(
    (leadId: number) => {
      // Credit check for single lead reveal
      if (Number(leadCredits) < 7) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.EMAIL_PHONE));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      setEmailPhoneRevealingLeads((prev) => ({
        ...prev,
        [leadId]: true,
      }));
      // Call reveal - the state update will trigger a re-render
      handleRevealLead(leadId, LeadRevealType.EMAIL_PHONE);
    },
    [handleRevealLead, leadCredits, setEmailPhoneRevealingLeads, dispatch],
  );

  const handleSaveAsProspect = useCallback(
    async (leadId: number) => {
      try {
        setLoadingAddToProspect((prev) => ({ ...prev, [leadId]: true }));
        const addToProspectRes = await leadAddToProspectAPI(leadId);
        if (addToProspectRes) {
          handleAppendLead(addToProspectRes?.payload);
          toaster.success(
            LEAD_LISTING_MESSAGES.add_to_prospect_single_lead_succeess_msg,
          );
        }

        dispatch(getPostLoadMetaRequest());
        setLoadingAddToProspect((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
      } catch (e: any) {
        setLoadingAddToProspect((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
        if (!e.isHandled && e.message) {
          toaster.error(e.message);
        }
      }
    },
    [handleAppendLead, dispatch],
  );

  const handleAddToSequence = useCallback(
    (leadId: number) => {
      dispatch(openAddToSequenceModal(leadId));
    },
    [dispatch],
  );

  const handleBulkAction = useCallback(
    (actionType: BulkLeadsActionType) => {
      const leadIds = selectedLeads;
      const leadFinderLimit =
        activeSubTab === LeadFinderSecondaryTab.SAVED
          ? SAVED_TAB_BULK_OPERATION_LIMIT
          : leadFinderBulkOperationLimit();

      switch (actionType) {
        case BulkLeadsActionType.REVEAL_EMAIL:
          handleBulkRevealWithTags(LeadRevealType.EMAIL);
          break;
        case BulkLeadsActionType.REVEAL_EMAIL_PHONE:
          handleBulkRevealWithTags(LeadRevealType.EMAIL_PHONE);
          break;
        case BulkLeadsActionType.ADD_TO_PROSPECT:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkActionFromHook(actionType);
          break;
        case BulkLeadsActionType.ADD_TO_SEQUENCE:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkActionFromHook(actionType);
          break;
        case BulkLeadsActionType.EXPORT:
          if (isDemoAccount) {
            dispatch(setShowGlobalRestrictionModal(true));
            return;
          }

          if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
            if (leadIds?.length > leadFinderLimit) {
              toaster.error(
                `Please select less than ${leadFinderLimit} leads to proceed!`,
              );
              return;
            }
            handleBulkActionFromHook(actionType);
          } else if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
            handleCompanyExport();
          }
          break;
        default:
          break;
      }
    },
    [
      selectedLeads,
      activeSubTab,
      activeLeadType,
      handleBulkActionFromHook,
      handleBulkRevealWithTags,
      handleCompanyExport,
    ],
  );

  const hideAddToSequenceModal = useCallback(() => {
    dispatch(closeAddToSequenceModal());
  }, [dispatch]);

  const handleRemoveFromListModalClose = useCallback(() => {
    dispatch(setShowRemoveFromListModal(false));
    setLeadIdToAddFromRow(null);
    leadIdToAddFromRowRef.current = null;
  }, [dispatch, setLeadIdToAddFromRow, leadIdToAddFromRowRef]);

  const handleRequestOpenRemoveFromList = useCallback(() => {
    dispatch(setShowRemoveFromListModal(true));
  }, [dispatch]);

  const handleCreateNewList = useCallback(() => {
    // TODO: Open create list modal
  }, []);

  const handleBulkRevealTagModalClose = useCallback(() => {
    dispatch(setShowBulkRevealTagModal(false));
    setTagsToAdd([]);
    setLeadRevealType(null);
  }, [dispatch, setTagsToAdd, setLeadRevealType]);

  const handleCreditModalHide = useCallback(() => {
    dispatch(closeCreditNotAvailableModal());
  }, [dispatch]);

  const handleCreditModalAddCredits = useCallback(() => {
    redirectWithoutRefresh(Routes.Setting_Billing_Add_Credits);
    dispatch(closeCreditNotAvailableModal());
  }, [dispatch]);

  const handleSelectedMoreThanLimitModalClose = useCallback(() => {
    dispatch(setShowSelectedMoreThanLimitModal(false));
  }, [dispatch]);

  const handleWhatsNewModalClose = useCallback(() => {
    setShowWhatsNewModal(false);
  }, []);

  const handleAiChatApplyUrl = useCallback(
    (url: string) => {
      try {
        const parsed = new URL(url);
        const params = parsed.searchParams;
        const targetTab = parsed.pathname.includes('/company/')
          ? LeadFinderPrimaryTab.COMPANY
          : LeadFinderPrimaryTab.PEOPLE;

        // Store current tab's filters before navigating (mirrors handleLeadTypeChange logic)
        const currentParams = new URLSearchParams(location.search);
        const currentTabKey = location.pathname.includes('/v2/leads/company/')
          ? 'company'
          : 'people';
        const currentFilters = getAllFiltersExceptPagination(currentParams);
        tabSpecificFiltersRef.current[
          currentTabKey
        ] = currentFilters.toString();

        history.push(
          `/v2/leads/${targetTab}/${
            LeadFinderSecondaryTab.TOTAL
          }?${params.toString()}`,
        );
      } catch (_) {
        // If URL parsing fails, ignore
      }
    },
    [
      history,
      location.search,
      location.pathname,
      getAllFiltersExceptPagination,
    ],
  );

  const handleSetSidebarExpanded = useCallback(
    (value: boolean) => {
      dispatch(setSidebarExpanded(value));
    },
    [dispatch],
  );

  const handleSendGetTeamsListRequest = useCallback(() => {
    dispatch(
      getTeamsRequest({
        pageNum: 1,
        pageSize: 500,
      }),
    );
  }, [dispatch]);

  const addContactToStep = useCallback(
    async (sequenceId: number, stepId: number) => {
      const leadId = selectedLeadIdForSequence;
      if (!leadId) return;

      try {
        setLoadingAddToSequence((prev) => ({ ...prev, [leadId]: true }));
        const response = await leadAddToSequenceAPI({
          leadId,
          sequenceId,
          stepId,
        });
        if (response?.payload?.failed) {
          if (response?.payload?.failedReport?.length) {
            const failedReport = response?.payload?.failedReport?.[0];
            const errorMessage =
              failedReport?.reason ||
              'Prospect already exists in the selected step';
            toaster.error(errorMessage, {
              theme: Theme.New,
            });
          } else {
            toaster.error('Prospect already exists in the selected step', {
              theme: Theme.New,
            });
          }
        } else if (response?.payload?.isAddedToSequence) {
          toaster.success(
            LEAD_LISTING_MESSAGES.add_to_sequence_single_lead_succeess_msg,
          );
        }

        if (response?.payload?.isContactCreated) {
          setLeadsData((prev) => ({
            ...prev,
            profiles: prev.profiles?.map((lead) =>
              lead.id === leadId ? { ...lead, isProspectCreated: true } : lead,
            ),
          }));
        }
        dispatch(getPostLoadMetaRequest());
      } catch (e: any) {
        if (!e.isHandled && e.message) {
          toaster.error(e.message);
        }
      } finally {
        setLoadingAddToSequence((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
      }
    },
    [selectedLeadIdForSequence, dispatch],
  );

  const handlePaginationChange = useCallback(
    (page: number, limit: number) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('page');
      urlParams.delete('start');
      urlParams.delete('take');
      const filterSource =
        !urlParams.toString() && tabSpecificFiltersRef.current.people
          ? tabSpecificFiltersRef.current.people
          : undefined;
      handlePagination({ page, limit, filterSource });
    },
    [handlePagination, tabSpecificFiltersRef],
  );

  const addToListData = useMemo(() => {
    if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
      return (companyListsData || []).map((list: any) => ({
        id: list.id,
        label: list.label,
        visibility: list.visibility,
        owner: list.owner,
        isDefault: list.isDefault,
      }));
    }
    return peopleListsData || [];
  }, [activeLeadType, companyListsData, peopleListsData]);

  const addToListModalProps = useMemo(
    () => ({
      teamsList,
      getTeamsListRequestStatus,
      sendGetTeamsListRequest: handleSendGetTeamsListRequest,
      onSaveList: handleSaveList,
      isUpdateListLoading: isCreateListLoading,
    }),
    [
      teamsList,
      getTeamsListRequestStatus,
      handleSendGetTeamsListRequest,
      handleSaveList,
      isCreateListLoading,
    ],
  );

  const leadsPagination = useMemo(() => {
    if (countsError && activeSubTab !== LeadFinderSecondaryTab.SAVED) {
      return undefined;
    }
    return {
      page: leadsData?.pagination?.page || 1,
      limit: leadsData?.pagination?.limit || 25,
      totalRows: leadsData?.pagination?.totalRows || 0,
    };
  }, [
    countsError,
    activeSubTab,
    leadsData?.pagination?.page,
    leadsData?.pagination?.limit,
    leadsData?.pagination?.totalRows,
  ]);

  const companyPagination = useMemo(() => {
    if (companyCountsError && activeSubTab !== LeadFinderSecondaryTab.SAVED) {
      return undefined;
    }
    return {
      page: companiesData?.pagination?.page || 1,
      limit: companiesData?.pagination?.limit || 25,
      totalRows: companiesData?.pagination?.totalRows || 0,
    };
  }, [
    companyCountsError,
    activeSubTab,
    companiesData?.pagination?.page,
    companiesData?.pagination?.limit,
    companiesData?.pagination?.totalRows,
  ]);

  const mergedTagButtonProps = useMemo(
    () =>
      tagButtonProps
        ? ({
            ...tagButtonProps,
            triggerOpenAddTag,
            triggerOpenRemoveTag,
            onRowTagModalClosed: handleRowTagModalClosed,
            overrideSelectedLeadObjects,
          } as any)
        : undefined,
    [
      tagButtonProps,
      triggerOpenAddTag,
      triggerOpenRemoveTag,
      handleRowTagModalClosed,
      overrideSelectedLeadObjects,
    ],
  );

  const mergedCompanyTagButtonProps = useMemo(
    () =>
      companyTagButtonProps
        ? {
            ...companyTagButtonProps,
            triggerOpenAddTag,
            triggerOpenRemoveTag,
            renderWhenTriggered: true,
            onRowTagModalClosed: handleRowTagModalClosed,
            overrideSelectedCompanyObjects,
          }
        : undefined,
    [
      companyTagButtonProps,
      triggerOpenAddTag,
      triggerOpenRemoveTag,
      handleRowTagModalClosed,
      overrideSelectedCompanyObjects,
    ],
  );

  const isTagsFilterApplied = useMemo(
    () => getTagsFilterApplied({ prospectsFilters }),
    [prospectsFilters],
  );

  // Determine which screen to show
  // For company tab, check companiesData; for people tab, check leadsData
  const currentData = isCompanyTab ? companiesData : leadsData;
  const currentIsLoading = isCompanyTab ? isCompanyLoading : isLoading;
  const storedUrls = isCompanyTab ? storedUrlsCompany : storedUrlsPeople;

  // Show when user hasn't searched yet (no API response with pagination)
  const showSearchingEmpty =
    !currentIsLoading &&
    activeSubTab !== LeadFinderSecondaryTab.SAVED &&
    storedUrls.length === 0 &&
    !location.search &&
    !currentData?.pagination;

  const showRecentSearch =
    !currentIsLoading &&
    activeSubTab !== LeadFinderSecondaryTab.SAVED &&
    storedUrls &&
    storedUrls.length > 0 &&
    !location.search &&
    !currentData?.pagination &&
    (!currentData?.profiles || currentData?.profiles?.length === 0);

  // Show when search API returned 0 results (works for URL-based and bulk search)
  const showNoResults =
    !currentIsLoading &&
    currentData &&
    currentData?.pagination &&
    (!currentData?.profiles || currentData?.profiles?.length === 0) &&
    currentData?.pagination?.totalRows === 0 &&
    !(activeSubTab === LeadFinderSecondaryTab.SAVED && !location.search);

  // Show table when loading OR when there are leads/companies (includes bulk search without URL)
  const showLeadsTable =
    currentIsLoading ||
    (currentData?.profiles &&
      currentData?.profiles?.length > 0 &&
      (location.search ||
        activeSubTab === LeadFinderSecondaryTab.SAVED ||
        (currentData?.pagination && currentData?.pagination?.totalRows > 0)));

  // For saved tab: show empty state when no leads/companies are saved and no search is applied
  const showSavedTabEmpty =
    !currentIsLoading &&
    activeSubTab === LeadFinderSecondaryTab.SAVED &&
    currentData?.pagination?.totalRows === 0 &&
    !location.search;

  // Don't render if API succeeded and user doesn't have access
  const shouldBlockAccessRender =
    getIsRequestSucceeded(getUserSettingsRequestStatus) &&
    !hasV2LeadFinderAccess;

  if (shouldBlockAccessRender) {
    return null;
  }

  return (
    <Container fluid className="v2-lead-container px-0">
      <ErrorBoundaryWrapper>
        <Suspense>
          <V2LeadHeader
            activeTab={activeLeadType}
            activeSubTab={activeSubTab}
            onTabChange={handleLeadTypeChange}
            isLoading={isLoading}
            leadCredits={leadCredits}
            selectedLeadsCount={selectedLeads.length}
            selectedLeads={selectedLeads}
            isSelectedAllLeads={isSelectedAllLeads}
            onBulkAction={handleBulkAction}
            selectedCompaniesCount={selectedCompanies.length}
            selectedCompanies={selectedCompanies}
            isSelectedAllCompanies={isSelectedAllCompanies}
            companiesData={companiesData}
            leadsData={leadsData}
            onCompanyBulkFindPeople={handleBulkFindPeople}
            isLoadingBulkFindPeople={isBulkFindPeopleLoading}
            addToListData={addToListData}
            addToListOpen={addToListOpenFromRow}
            onAddToListOpenChange={handleAddToListOpenChange}
            addFromRowCompanyId={companyIdToAddFromRow}
            addFromRowLeadId={leadIdToAddFromRow}
            onAddToList={handleAddToList}
            onRemoveFromList={handleRemoveFromList}
            onCreateNewList={handleCreateNewList}
            addToListModalProps={addToListModalProps}
            loadingBulkAction={loadingBulkAction}
            loadingRemoveFromList={loadingRemoveFromList}
            showRemoveFromListModal={showRemoveFromListModal}
            onRemoveFromListModalClose={handleRemoveFromListModalClose}
            onRequestOpenRemoveFromList={handleRequestOpenRemoveFromList}
            tagButtonProps={mergedTagButtonProps}
            companyTagButtonProps={mergedCompanyTagButtonProps}
          />
          <Row className="m-0">
            <V2LeadSidebar
              isSidebarExpanded={isSidebarExpanded}
              setIsSidebarExpanded={handleSetSidebarExpanded}
              handleRecentSearch={handleRecentSearchWithRefresh}
              handleGeneratePayload={
                handlePeopleGeneratePayloadWithBulkFallback
              }
              setSelectedLeads={(val) =>
                dispatch(setSelectedLeads(val as number[]))
              }
              showPhoneNumberNudge={showPhoneNumberNudge}
              setShowPhoneNumberNudge={setShowPhoneNumberNudge}
              setIgnorePhoneNumberNudge={setIgnorePhoneNumberNudge}
              searchLeads={searchLeads}
              isLoading={isLoading}
              isLoadingCounts={isLoadingCounts}
              isLoadingSavedCounts={isLoadingSavedCounts}
              keywordInputRef={keywordInputRef}
              activeSubTab={activeSubTab}
              onFilterTabChange={handleSubTabChange}
              leadTags={leadTags}
              isLeadTagsLoading={isLeadTagsLoading}
              savedFilters={savedFilters}
              setSavedFilters={setSavedFilters}
              countsData={countsData}
              countsError={countsError}
              handleRouteChange={handleRouteChange}
              // Company-specific props
              activeLeadType={activeLeadType}
              searchCompanies={isCompanyTab ? searchCompanies : undefined}
              setSelectedCompanies={(val) =>
                dispatch(setSelectedCompanies(val as number[]))
              }
              companyCountsData={companyCountsData}
              companyCountsError={companyCountsError}
              isCompanyLoading={isCompanyLoading}
              isLoadingCompanyCounts={isLoadingCompanyCounts}
              isLoadingCompanySavedCounts={isLoadingCompanySavedCounts}
              handleCompanyGeneratePayload={handleCompanyGeneratePayload}
              companyListsData={companyListsData}
              peopleTabCompanyListsData={peopleTabCompanyListsData}
              isLoadingCompanyLists={isLoadingCompanyLists}
              isLoadingPeopleLists={isLoadingPeopleLists}
              peopleListsData={peopleListsData}
              onSaveList={handleSaveList}
              onCreateListOnly={handleCreateListOnly}
              isUpdateListLoading={isCreateListLoading}
              onExportList={handleExportList}
              onDeleteList={handleDeleteList}
              isDeleteListLoading={isDeleteListLoading}
              teamsList={teamsList}
              getTeamsListRequestStatus={getTeamsListRequestStatus}
              sendGetTeamsListRequest={handleSendGetTeamsListRequest}
              hasPaymentPendingBanner={hasPaymentPendingBanner}
              signalsListData={signalsListData}
              newsListData={newsListData}
              handleCompanyRecentSearch={handleCompanyRecentSearchWithRefresh}
              loginUserEmail={loginUserEmail}
              triggerSearchWithFilters={
                isCompanyTab
                  ? handleCompanyTriggerSearchWithFilters
                  : handlePeopleTriggerSearchWithFilters
              }
              onClearAll={
                isCompanyTab ? handleClearAllCompany : handleClearAllPeople
              }
              skipUrlPushForBulkSearchRef={skipUrlPushForBulkSearchRef}
              tabSpecificFiltersRef={tabSpecificFiltersRef}
              tabSpecificBulkModeRef={tabSpecificBulkModeRef}
              syncCompanyTabSpecificFiltersRef={
                syncCompanyTabSpecificFiltersRef
              }
              syncPeopleTabSpecificFiltersRef={syncPeopleTabSpecificFiltersRef}
            />

            <Col className={mainContentClass}>
              {/* Show empty state when no search history */}
              {showSearchingEmpty && <SearchingEmpty />}

              {/* Show recent searches */}
              {showRecentSearch && (
                <RecentSearch
                  storedUrls={storedUrls}
                  setStoredUrls={
                    isCompanyTab ? setStoredUrlsCompany : setStoredUrlsPeople
                  }
                  loginUserEmail={loginUserEmail}
                  searchLeads={searchLeads}
                  searchCompanies={searchCompanies}
                  storageKey={
                    isCompanyTab
                      ? V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY
                      : V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY
                  }
                  companyListsData={companyListsData}
                  peopleListsData={peopleListsData}
                />
              )}

              {/* Show no results for total/new tabs, and for saved tab when a search is applied */}
              {showNoResults && (
                <div className="v2-no-search-lead">
                  <NoSearchResult
                    description={noResultsDescription}
                    title="No results found"
                  >
                    <NoSearchResultIcon />
                  </NoSearchResult>
                </div>
              )}

              {/* Show empty state for saved tab */}
              {showSavedTabEmpty && (
                <div className="v2-no-search-lead">
                  <NoSearchResult
                    description={noSavedLeadsDescription}
                    title="No saved leads"
                  >
                    <NoSearchResultIcon />
                  </NoSearchResult>
                </div>
              )}

              {/* Show leads table - conditionally render People or Company table */}
              <Suspense
                fallback={
                  <TableLoadingSkeleton
                    tab={isCompanyTab ? 'companies' : 'leads'}
                  />
                }
              >
                {showLeadsTable && !isCompanyTab && (
                  <div className="v2-lead-table-wrapper">
                    <V2LeadTable
                      leads={leadsData?.profiles || []}
                      isLoading={isLoading}
                      selectedLeadIds={selectedLeads}
                      onLeadSelect={handleLeadSelect}
                      onLeadSelectAll={handleLeadSelectAll}
                      onLeadClick={handleLeadClick}
                      pagination={leadsPagination}
                      onPaginationChange={handlePaginationChange}
                      onRevealEmail={handleRevealEmail}
                      onRevealEmailPhone={handleRevealEmailPhone}
                      onSaveAsProspect={handleSaveAsProspect}
                      onAddToSequence={handleAddToSequence}
                      emailRevealingLeads={emailRevealingLeads}
                      emailPhoneRevealingLeads={emailPhoneRevealingLeads}
                      loadingAddToProspect={loadingAddToProspect}
                      loadingAddToSequence={loadingAddToSequence}
                      activeSubTab={activeSubTab}
                      onSelectAllAcrossPages={handleSelectAllAcrossPages}
                      isSelectedAllLeads={isSelectedAllLeads}
                      selectAllRevealType={selectAllRevealType}
                      onRowAddToList={handleSingleLeadAddToList}
                      onRowRemoveFromList={handleLeadRowRemoveFromList}
                      onRowAddTag={handleLeadRowAddTag}
                      onRowRemoveTag={handleLeadRowRemoveTag}
                      hasListPermission={
                        hasPermission(Permissions.ACCOUNT_USER_READ) ||
                        hasPermission(Permissions.TEAM_USER_READ)
                      }
                      onRevealCompany={handleRevealCompanyWithCreditCheck}
                      enrichingCompanies={enrichingCompanies}
                      companiesProfiles={companiesData?.profiles}
                    />
                  </div>
                )}

                {/* Show company table when Company tab is active */}
                {isCompanyTab &&
                  (isCompanyLoading ||
                    (companiesData?.profiles &&
                      companiesData?.profiles?.length > 0 &&
                      (location.search ||
                        activeSubTab === LeadFinderSecondaryTab.SAVED ||
                        (companiesData?.pagination &&
                          companiesData?.pagination?.totalRows > 0)))) && (
                    <div className="v2-lead-table-wrapper">
                      <V2CompanyTable
                        companies={companiesData?.profiles || []}
                        isLoading={isCompanyLoading}
                        selectedCompanyIds={selectedCompanies}
                        onRevealCompany={handleRevealCompanyWithCreditCheck}
                        enrichingCompanies={enrichingCompanies}
                        onCompanySelect={handleCompanySelect}
                        onCompanySelectAll={handleCompanySelectAll}
                        onSelectAllAcrossPages={handleSelectAllAcrossPages}
                        isSelectedAllCompanies={isSelectedAllCompanies}
                        pagination={companyPagination}
                        onPaginationChange={handleCompanyPaginationChange}
                        activeSubTab={activeSubTab}
                        onSaveCompany={handleSaveCompanyFromRow}
                        onFindPeople={handleFindPeople}
                        onRowAddToList={handleRowAddToList}
                        onRowRemoveFromList={handleRowRemoveFromList}
                        onRowAddTag={handleRowAddTag}
                        onRowRemoveTag={handleRowRemoveTag}
                        hasListPermission={
                          hasPermission(Permissions.ACCOUNT_USER_READ) ||
                          hasPermission(Permissions.TEAM_USER_READ)
                        }
                      />
                    </div>
                  )}
              </Suspense>
            </Col>
          </Row>
        </Suspense>
      </ErrorBoundaryWrapper>

      {showAddToSequenceModal && (
        <Suspense>
          <AddToSequenceModal
            show={showAddToSequenceModal}
            onHide={hideAddToSequenceModal}
            addToSequence={
              selectedLeadIdForSequence
                ? addContactToStep
                : handleBulkAddToSequence
            }
            isSubscribed={true}
          />
        </Suspense>
      )}

      {showProspectDetailsModal && selectedContactId && (
        <Suspense>
          <ProspectDetails
            show={showProspectDetailsModal}
            onHide={hideShowProspectDetailsModal}
            selectedProspectId={selectedContactId}
            resetSelectedProspectId={hideShowProspectDetailsModal}
            selectedProspectData={singleProspect}
            selectedProspectSequences={singleProspectSequenceDetails}
            getSingleProspectDetailsStatus={
              getSingleProspectDetailsRequest.status
            }
            getSingleProspectSequencesDetailsStatus={
              getSingleProspectSequencesDetailsRequest.status
            }
            isTagsFilterApplied={isTagsFilterApplied}
            defaultTab={defaultTab}
          />
        </Suspense>
      )}

      {showBulkRevealTagModal && (
        <Suspense>
          <BulkRevealTagModal
            show={showBulkRevealTagModal}
            onClose={handleBulkRevealTagModalClose}
            onSubmit={handleBulkRevealSubmit}
            isLoading={isBulkRevealing}
            selectedLeadsCount={selectedLeads.length}
            isSelectedAllLeads={isSelectedAllLeads}
            selectedAllLeadsCount={
              leadsData?.pagination?.totalRows >
              leadFinderBulkOperationLimitValue
                ? leadFinderBulkOperationLimitValue
                : leadsData?.pagination?.totalRows || 0
            }
            leadTags={leadTags}
            tagsToAdd={tagsToAdd}
            setTagsToAdd={setTagsToAdd}
          />
        </Suspense>
      )}

      {/* Credit Not Available Modal */}
      {showCreditNotAvailableModal && creditCheckRevealType && (
        <Suspense>
          <CreditNotAvailableModal
            show={showCreditNotAvailableModal}
            onHide={handleCreditModalHide}
            onAddCredits={handleCreditModalAddCredits}
            revealType={creditCheckRevealType}
          />
        </Suspense>
      )}

      {/* Selected More Than Limit Modal */}
      {showSelectedMoreThanLimitModal && (
        <Suspense>
          <SelectedMoreThanLimitModal
            show={showSelectedMoreThanLimitModal}
            onClose={handleSelectedMoreThanLimitModalClose}
          />
        </Suspense>
      )}

      {/* What's New Modal - shown when user switches from old to new lead finder */}
      {showWhatsNewModal && (
        <Suspense>
          <WhatsNewModal
            show={showWhatsNewModal}
            onClose={handleWhatsNewModalClose}
          />
        </Suspense>
      )}

      {showAiSearchPopup && (
        <Suspense>
          <AiSearchPopup
            show={showAiSearchPopup}
            onClose={() => dispatch(setShowAiSearchPopup(false))}
          />
        </Suspense>
      )}
      <Suspense>
        <GlobalRestrictionModal
          show={showGlobalRestrictionModal}
          onClose={() => dispatch(setShowGlobalRestrictionModal(false))}
          title="Exporting data is disabled in demo account"
          content="This is a demo account with sample data. Signup to start exporting the data"
        />
      </Suspense>
      {!showAiChatPanel && !showSearchingEmpty && !showRecentSearch && (
        <button
          type="button"
          className="ai-chat-fab"
          onClick={() => dispatch(openAiChatPanel())}
          title="Sage"
        >
          <NoResultGradientImage />
        </button>
      )}
      <Suspense>
        <AiChatPanel
          show={showAiChatPanel}
          onClose={() => dispatch(closeAiChatPanel())}
          onApplyUrl={handleAiChatApplyUrl}
          pendingQuery={aiChatPendingQuery}
          onPendingQueryConsumed={() => dispatch(clearAiChatPendingQuery())}
          onConversationIdChange={(id) =>
            dispatch(setActiveChatConversationId(id))
          }
          initialView={aiChatInitialView}
          onInitialViewConsumed={() => dispatch(resetAiChatInitialView())}
        />
      </Suspense>
    </Container>
  );
};

export default V2Lead;
